import React, { useEffect, useMemo, useState } from 'react';
import type { Table, Order, Reservation } from '../../types';
import {
  evaluatePartyTableAssignment,
  getMaxSingleTableCapacity,
  getTablesFreeForSlot,
  isAdjacentTableCluster,
  reservationHoldsTable,
  RESERVATION_TABLE_BUFFER_MINUTES,
  suggestJoinCombinations,
  suggestTablesToJoin,
} from '../../lib/reservationCapacity';

interface OrderTransferModalProps {
  sourceTable: Table;
  /** When transferring an unpaid dine-in/buffet order */
  order?: Order;
  /** When transferring a reservation before an order exists */
  reservationSummary?: {
    customerName: string;
    partySize: number;
    time: string;
    status: string;
    reservationDate?: string;
    reservationId?: number;
    requiresMerge?: boolean;
    assignedTableIds?: number[];
  };
  availableTables: Table[];
  /** All tables + reservations — used for join suggestions on reservation transfer */
  allTables?: Table[];
  reservations?: Reservation[];
  onConfirm: (targetTableId: number, options?: { assignedTableIds?: number[] }) => void;
  onCancel: () => void;
  title?: string;
}

const OrderTransferModal: React.FC<OrderTransferModalProps> = ({
  sourceTable,
  order,
  reservationSummary,
  availableTables,
  allTables = [],
  reservations = [],
  onConfirm,
  onCancel,
  title,
}) => {
  const partySize = reservationSummary?.partySize ?? 0;
  const currentlyJoined =
    Boolean(reservationSummary?.requiresMerge) ||
    (reservationSummary?.assignedTableIds?.length ?? 0) > 1;
  const maxSingle = useMemo(
    () => getMaxSingleTableCapacity(allTables.length ? allTables : availableTables),
    [allTables, availableTables],
  );
  const partyNeedsJoin = partySize > maxSingle && maxSingle > 0;
  const showJoinOption = Boolean(reservationSummary) && (currentlyJoined || partyNeedsJoin);

  const [transferMode, setTransferMode] = useState<'single' | 'join'>(
    currentlyJoined || partyNeedsJoin ? 'join' : 'single',
  );
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  const [assignedTableIds, setAssignedTableIds] = useState<number[]>([]);

  useEffect(() => {
    if (!showJoinOption) {
      setTransferMode('single');
      return;
    }
    setTransferMode(currentlyJoined || partyNeedsJoin ? 'join' : 'single');
  }, [showJoinOption, currentlyJoined, partyNeedsJoin]);

  const freeForJoin = useMemo(() => {
    if (!reservationSummary?.reservationDate || !reservationSummary.time) {
      return availableTables;
    }
    const pool = allTables.length ? allTables : availableTables;
    const free = getTablesFreeForSlot(
      pool,
      reservations,
      reservationSummary.reservationDate,
      reservationSummary.time,
      reservationSummary.reservationId,
      RESERVATION_TABLE_BUFFER_MINUTES,
    );
    return free.filter((t) => {
      const heldByThis =
        reservationSummary.reservationId != null &&
        reservations.some(
          (r) =>
            Number(r.id) === Number(reservationSummary.reservationId) &&
            reservationHoldsTable(r, t.id),
        );
      if (heldByThis) return true;
      if (t.status !== 'available') return false;
      if (t.mergedWith && t.mergedWith.length > 1) return false;
      return true;
    });
  }, [allTables, availableTables, reservations, reservationSummary]);

  const assignment = useMemo(
    () => evaluatePartyTableAssignment(freeForJoin, partySize || 2, maxSingle),
    [freeForJoin, partySize, maxSingle],
  );

  useEffect(() => {
    if (transferMode !== 'join' || !showJoinOption) return;
    if (assignedTableIds.length > 0) return;
    const suggested = suggestTablesToJoin(freeForJoin, partySize || 2);
    if (suggested.length >= 2) {
      setAssignedTableIds(suggested.map((t) => Number(t.id)));
    }
  }, [transferMode, showJoinOption, freeForJoin, partySize, assignedTableIds.length]);

  const selectedJoinCapacity = useMemo(
    () =>
      assignedTableIds.reduce((sum, id) => {
        const t = freeForJoin.find((x) => Number(x.id) === Number(id))
          ?? allTables.find((x) => Number(x.id) === Number(id))
          ?? availableTables.find((x) => Number(x.id) === Number(id));
        return sum + (t?.capacity ?? 0);
      }, 0),
    [assignedTableIds, freeForJoin, allTables, availableTables],
  );

  const joinOk =
    assignedTableIds.length >= 2 && selectedJoinCapacity >= (partySize || 1);

  const selectedJoinAdjacent = useMemo(() => {
    const selected = assignedTableIds
      .map(
        (id) =>
          freeForJoin.find((t) => Number(t.id) === Number(id))
          ?? allTables.find((t) => Number(t.id) === Number(id)),
      )
      .filter(Boolean) as Table[];
    return selected.length >= 2 && isAdjacentTableCluster(selected);
  }, [assignedTableIds, freeForJoin, allTables]);

  const primaryCombo = useMemo(() => {
    if (assignment.mode === 'impossible') return null;
    const suggested = suggestTablesToJoin(freeForJoin, partySize || 2);
    if (suggested.length < 2) return null;
    return {
      tables: suggested,
      capacity: suggested.reduce((s, t) => s + t.capacity, 0),
      adjacent: isAdjacentTableCluster(suggested),
    };
  }, [assignment.mode, freeForJoin, partySize]);

  const alternateCombos = useMemo(() => {
    if (!showJoinOption || assignment.mode === 'impossible') return [];
    const currentKey = [...assignedTableIds].map(Number).sort((a, b) => a - b).join(',');
    return suggestJoinCombinations(freeForJoin, partySize || 2, {
      limit: 5,
      ...(assignedTableIds.length > 0 && assignedTableIds.length < 2
        ? { mustIncludeTableIds: assignedTableIds }
        : {}),
    }).filter((c) => {
      const ids = c.tables.map((t) => Number(t.id)).sort((a, b) => a - b).join(',');
      return ids !== currentKey;
    });
  }, [showJoinOption, assignment.mode, freeForJoin, partySize, assignedTableIds]);

  const toggleJoinTable = (id: number) => {
    setAssignedTableIds((prev) =>
      prev.some((x) => Number(x) === Number(id))
        ? prev.filter((x) => Number(x) !== Number(id))
        : [...prev, id],
    );
  };

  const applyJoinCombination = (ids: number[]) => {
    setAssignedTableIds(ids.map(Number));
  };

  const canTransferSingle = selectedTableId !== null && availableTables.length > 0;
  const canTransferJoin = joinOk;
  const canTransfer = transferMode === 'join' && showJoinOption ? canTransferJoin : canTransferSingle;
  const heading = title ?? (reservationSummary ? 'Transfer Reservation' : 'Transfer Order');

  const sourceJoinLabel =
    currentlyJoined && reservationSummary?.assignedTableIds?.length
      ? reservationSummary.assignedTableIds
          .map((id) => allTables.find((t) => Number(t.id) === Number(id))?.name ?? `Table ${id}`)
          .join(' + ')
      : sourceTable.name;

  const handleConfirm = () => {
    if (transferMode === 'join' && showJoinOption) {
      if (!joinOk || assignedTableIds.length < 2) return;
      const primary = Number(assignedTableIds[0]);
      onConfirm(primary, { assignedTableIds: assignedTableIds.map(Number) });
      return;
    }
    if (selectedTableId != null) onConfirm(selectedTableId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden my-auto">
        <header className="p-4 border-b flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-800">{heading}</h2>
        </header>
        <main className="p-4 flex-1 min-h-0 overflow-y-auto">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Transfer from:</p>
            <div className="p-3 bg-gray-50 rounded border">
              <p className="font-semibold">{sourceJoinLabel}</p>
              {order && (
                <p className="text-xs text-gray-500 mt-1">
                  Order ID: {order.id.substring(0, 8)}... | Items: {order.items.length}
                </p>
              )}
              {reservationSummary && (
                <p className="text-xs text-gray-500 mt-1">
                  {reservationSummary.customerName} · party {reservationSummary.partySize} ·{' '}
                  {reservationSummary.time} · {reservationSummary.status}
                  {currentlyJoined ? ' · joined' : ''}
                </p>
              )}
            </div>
          </div>

          {showJoinOption && (
            <div className="mb-4 flex rounded-lg border border-gray-200 p-1 bg-gray-50 gap-1">
              <button
                type="button"
                onClick={() => setTransferMode('single')}
                className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  transferMode === 'single'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Single table
              </button>
              <button
                type="button"
                onClick={() => setTransferMode('join')}
                className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  transferMode === 'join'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Join tables
              </button>
            </div>
          )}

          {transferMode === 'join' && showJoinOption ? (
            <div className="mb-4 space-y-3">
              <p className="text-sm text-gray-600">
                Pick a new join set (or change which tables are merged). Combined seats must cover
                the party of {partySize}.
              </p>
              {assignment.mode === 'impossible' ? (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    Not enough free capacity for this party. Free tables or transfer as a single
                    table with a smaller set.
                  </p>
                </div>
              ) : (
                <>
                  {primaryCombo && (
                    <p className="text-xs text-gray-700">
                      Suggested:{' '}
                      <button
                        type="button"
                        className="font-semibold text-pink-700 hover:underline"
                        onClick={() =>
                          applyJoinCombination(primaryCombo.tables.map((t) => Number(t.id)))
                        }
                      >
                        {primaryCombo.tables.map((t) => t.name).join(' + ')} ({primaryCombo.capacity}{' '}
                        seats)
                      </button>
                    </p>
                  )}
                  {alternateCombos.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {alternateCombos.map((combo) => {
                        const ids = combo.tables.map((t) => Number(t.id));
                        return (
                          <button
                            key={ids.join('-')}
                            type="button"
                            onClick={() => applyJoinCombination(ids)}
                            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800 hover:border-pink-400 hover:bg-pink-50"
                          >
                            {combo.tables.map((t) => t.name).join(' + ')} ({combo.capacity})
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="max-h-52 overflow-y-auto space-y-2">
                    {freeForJoin.map((table) => {
                      const checked = assignedTableIds.some((id) => Number(id) === Number(table.id));
                      return (
                        <label
                          key={table.id}
                          className={`flex items-center gap-2 w-full p-3 rounded border cursor-pointer transition-colors ${
                            checked
                              ? 'bg-blue-100 border-blue-400'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleJoinTable(Number(table.id))}
                            className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                          />
                          <span>
                            <span className="font-semibold block">{table.name}</span>
                            <span className="text-xs text-gray-500">Capacity: {table.capacity}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {assignedTableIds.length > 0 && (
                    <p
                      className={`text-sm font-medium ${
                        joinOk ? 'text-green-700' : 'text-amber-800'
                      }`}
                    >
                      Selected {selectedJoinCapacity} seats
                      {joinOk
                        ? ' — ready to transfer'
                        : ` — need ${Math.max(0, (partySize || 0) - selectedJoinCapacity)} more`}
                      {assignedTableIds.length < 2 ? ' (pick at least 2 tables)' : ''}.
                    </p>
                  )}
                  {assignedTableIds.length >= 2 && !selectedJoinAdjacent && (
                    <p className="text-xs text-amber-800 font-medium">
                      Selected tables are not next to each other on the floor — review before
                      confirming.
                    </p>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Select target table:</p>
              {availableTables.length === 0 ? (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-700">No available tables found.</p>
                </div>
              ) : (
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {availableTables.map((table) => (
                    <button
                      key={table.id}
                      type="button"
                      onClick={() => setSelectedTableId(table.id)}
                      className={`w-full p-3 text-left rounded border transition-colors ${
                        selectedTableId === table.id
                          ? 'bg-blue-100 border-blue-400'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <p className="font-semibold">{table.name}</p>
                      <p className="text-xs text-gray-500">Capacity: {table.capacity}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
        <footer className="p-4 border-t flex justify-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canTransfer}
            className={`px-4 py-2 text-white rounded transition-colors ${
              canTransfer
                ? 'bg-blue-500 hover:bg-blue-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {reservationSummary
              ? transferMode === 'join' && showJoinOption
                ? 'Transfer to joined tables'
                : 'Transfer Reservation'
              : 'Transfer Order'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default OrderTransferModal;
